/**
 * Today at a glance: calories left, macros, expenditure, trend weight, and the
 * few insights worth reading. Everything here is derived from local data.
 */

import { useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'

import { db } from '../db'
import { addWeighin } from '../db/mutations'
import { useModel, useProfile } from '../db/queries'
import { useApp } from '../context'
import { MacroBars } from '../components/charts/MacroBars'
import { Ring } from '../components/charts/Ring'
import { LineChart } from '../components/charts/LineChart'
import { checkinDue } from '../lib/checkin'
import { addDays, shortDate } from '../lib/days'
import { confidenceLabel } from '../lib/expenditure'
import { insights } from '../lib/insights'
import { expenditureOn } from '../lib/model'
import { Training } from '../components/Training'
import { fmtKcal } from '../lib/nutrients'
import { displayToKg, formatRate, formatWeight, kgToDisplay } from '../lib/units'

export function DashboardScreen() {
  const { unit, openAdd, goTo } = useApp()
  const m = useModel()
  const profile = useProfile()
  const lastCheckin = useLiveQuery(async () => {
    const all = (await db.checkins.orderBy('day').toArray()).filter((c) => !c.deleted_at)
    return all.length ? all[all.length - 1].day : null
  }, [])
  if (!m || profile === undefined) return <p className="muted">Loading…</p>

  const today = m.today
  const t = m.totals.get(today)
  const target = m.targetOn(today)
  const due = profile ? checkinDue(today, lastCheckin ?? null, profile.program_start ?? null, profile.checkin_weekday) : false
  const list = insights({
    days: m.summaries, today, weighDays: m.weighDays, lastWeighIn: m.lastWeighIn, rate14: m.rate14,
    goalType: profile?.goal_type ?? null, eNow: m.eNow, e28: expenditureOn(m, addDays(today, -28)), sodiumLimit: 2300,
  }).slice(0, 3)
  const trend30 = m.trend.filter((p) => p.day >= addDays(today, -30))

  return (
    <div>
      <h1>Today <span className="muted" style={{ fontWeight: 400, fontSize: '1rem' }}>· {shortDate(today)}</span></h1>

      {!profile?.sex && (
        <div className="card" style={{ marginBottom: '1rem', borderColor: 'var(--accent)' }}>
          <b>Set up your plan</b>
          <p className="muted small" style={{ margin: '.3rem 0 0' }}>A few details about you and your goal, and Macro works out your targets.</p>
          <button onClick={() => goTo('plan')}>Get started</button>
        </div>
      )}

      <div className="card">
        <div className="row" style={{ alignItems: 'center', gap: '1rem' }}>
          <Ring eaten={t?.kcal ?? 0} target={target?.kcal ?? null} size={140} />
          <MacroBars values={{ protein_g: t?.protein_g ?? 0, carbs_g: t?.carbs_g ?? 0, fat_g: t?.fat_g ?? 0 }} targets={target} />
        </div>
        <div className="muted small" style={{ marginTop: '.6rem' }}>
          {fmtKcal(t?.kcal ?? 0)} eaten{target ? ` of ${fmtKcal(target.kcal)} kcal` : ''}
        </div>
      </div>

      <Training day={today} target={target} isToday />

      <div className="grid3" style={{ marginTop: '.75rem' }}>
        <button className="secondary" style={{ margin: 0 }} onClick={() => openAdd({ day: today, mode: 'scan' })}>Scan</button>
        <button className="secondary" style={{ margin: 0 }} onClick={() => openAdd({ day: today, mode: 'photo' })}>Photo</button>
        <button className="secondary" style={{ margin: 0 }} onClick={() => openAdd({ day: today, mode: 'search' })}>Search</button>
      </div>

      {due && (
        <div className="card" style={{ marginTop: '1rem', borderColor: 'var(--accent)' }}>
          <b>Your weekly check-in is ready</b>
          <p className="muted small" style={{ margin: '.3rem 0 0' }}>See how the week went and update your targets.</p>
          <button onClick={() => goTo('plan')}>Check in</button>
        </div>
      )}

      <div className="grid2" style={{ marginTop: '1rem' }}>
        <div className="card" style={{ padding: '1rem' }}>
          <div className="muted small">Expenditure</div>
          <div className="big num">{m.eNow ? fmtKcal(m.eNow) : '—'}</div>
          <div className="muted small">{m.eNow ? `kcal/day · ${confidenceLabel(m.confidence)} confidence` : 'Set up your plan'}</div>
        </div>
        <div className="card" style={{ padding: '1rem' }}>
          <div className="muted small">Trend weight</div>
          <div className="big num">{m.trendNow ? formatWeight(m.trendNow, unit) : '—'}</div>
          <div className="muted small">{m.rate21 !== null ? formatRate(m.rate21, unit) : 'Needs 3+ weigh-ins'}</div>
        </div>
      </div>

      <WeighInQuick hasToday={m.weighDays.has(today)} />

      {trend30.length >= 2 && (
        <div className="card" style={{ marginTop: '1rem' }}>
          <div className="row"><b>Last 30 days</b><button className="linky" onClick={() => goTo('trends')}>Trends</button></div>
          <LineChart height={140} format={(v) => (kgToDisplay(v, unit) ?? 0).toFixed(1)} series={[
            { id: 'scale', label: 'Scale', color: 'var(--scale-dot)', kind: 'dots', points: trend30.map((p) => ({ day: p.day, y: p.kg })) },
            { id: 'trend', label: 'Trend', color: 'var(--protein)', points: trend30.map((p) => ({ day: p.day, y: p.trend })) },
          ]} />
        </div>
      )}

      {list.length > 0 && (
        <>
          <h2>Insights</h2>
          {list.map((i) => (
            <div key={i.id} className="card" style={{ padding: '.85rem 1rem', marginTop: '.5rem',
              borderLeft: `3px solid ${i.tone === 'good' ? 'var(--accent)' : i.tone === 'warn' ? 'var(--warn)' : 'var(--border)'}` }}>
              <b>{i.title}</b>
              <div className="muted small">{i.body}</div>
            </div>
          ))}
          <button className="linky" style={{ marginTop: '.6rem' }} onClick={() => goTo('stats')}>All stats</button>
        </>
      )}
    </div>
  )
}

function WeighInQuick({ hasToday }: { hasToday: boolean }) {
  const { unit } = useApp()
  const [v, setV] = useState('')
  const [saved, setSaved] = useState(false)
  if (hasToday && !saved) return null
  const kg = displayToKg(Number(v), unit)
  const ok = kg !== null && kg >= 20 && kg <= 400
  return (
    <div className="card" style={{ marginTop: '1rem', padding: '1rem' }}>
      {saved ? <span className="small" style={{ color: 'var(--accent)' }}>✓ Weigh-in saved — it shows up in Lift too.</span> : (
        <>
          <label htmlFor="wq" style={{ marginTop: 0 }}>Today's weigh-in ({unit})</label>
          <div className="row" style={{ gap: '.5rem' }}>
            <input id="wq" inputMode="decimal" value={v} onChange={(e) => setV(e.target.value)} placeholder="Morning, after the bathroom" />
            <button style={{ marginTop: 0 }} disabled={!ok} onClick={async () => {
              await addWeighin(kg!, new Date().toISOString())
              setV(''); setSaved(true)
            }}>Save</button>
          </div>
        </>
      )}
    </div>
  )
}
